// src/contexts/UploadQueueContext.jsx
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as fileService from '../services/file.service';

const UploadQueueContext = createContext();

export const UploadQueueProvider = ({ children }) => {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isQueueVisible, setIsQueueVisible] = useState(false);
  const [isQueueMinimized, setIsQueueMinimized] = useState(false);
  const [activeUploads, setActiveUploads] = useState(new Map());
  const [concurrencyLimits] = useState({
    smallFiles: 3, // Max 3 small files simultaneously
    largeFiles: 1   // Max 1 large file at a time
  });

  const abortControllersRef = useRef(new Map());

  // Load state from sessionStorage on mount
  useEffect(() => {
    const savedQueue = sessionStorage.getItem('uploadQueue');
    const savedVisibility = sessionStorage.getItem('uploadQueueVisible');
    const savedMinimized = sessionStorage.getItem('uploadQueueMinimized');

    if (savedQueue) {
      try {
        const parsedQueue = JSON.parse(savedQueue);
        // Filter out completed uploads on reload, keep pending and failed
        const filteredQueue = parsedQueue.filter(item =>
          item.status === 'pending' || item.status === 'failed'
        );
        setUploadQueue(filteredQueue);
      } catch (error) {
        console.error('Error loading upload queue from storage:', error);
      }
    }

    if (savedVisibility === 'true') setIsQueueVisible(true);
    if (savedMinimized === 'true') setIsQueueMinimized(true);
  }, []);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('uploadQueue', JSON.stringify(uploadQueue));
  }, [uploadQueue]);

  useEffect(() => {
    sessionStorage.setItem('uploadQueueVisible', isQueueVisible.toString());
  }, [isQueueVisible]);

  useEffect(() => {
    sessionStorage.setItem('uploadQueueMinimized', isQueueMinimized.toString());
  }, [isQueueMinimized]);

  const addToQueue = useCallback((files, targetPath = '', uploadType = 'files') => {


    const filesArray = Array.from(files);

    // Check if this is a folder upload (files have webkitRelativePath)
    const hasFolderStructure = filesArray.some(file => file.webkitRelativePath);

    if (hasFolderStructure || uploadType === 'folder') {

      // Create a single queue item for the entire folder
      const totalSize = filesArray.reduce((sum, file) => sum + file.size, 0);
      const folderName = filesArray[0].webkitRelativePath ?
        filesArray[0].webkitRelativePath.split('/')[0] :
        'Folder Upload';

      const batchItem = {
        id: uuidv4(),
        files: filesArray, // Store ALL files
        fileName: `${folderName} (${filesArray.length} files)`,
        fileSize: totalSize,
        targetPath,
        uploadType: 'folder',
        status: 'pending',
        progress: 0,
        message: '',
        error: null,
        isLarge: totalSize > 10 * 1024 * 1024,
        addedAt: Date.now(),
        isBatch: true, // Flag to identify batch operations
        fileCount: filesArray.length
      };


      setUploadQueue(prev => {
        const newQueue = [...prev, batchItem];
        return newQueue;
      });

      setIsQueueVisible(true);
      setTimeout(() => processQueue(), 100);

      return [batchItem.id];
    } else {
      // Handle individual file uploads (original logic)

      const newItems = filesArray.map(file => ({
        id: uuidv4(),
        file,
        fileName: file.name,
        fileSize: file.size,
        targetPath,
        uploadType: 'files',
        status: 'pending',
        progress: 0,
        message: '',
        error: null,
        isLarge: file.size > 10 * 1024 * 1024,
        multipart: {
          uploadId: null,
          key: null,
          bucket: 'files'
        },
        addedAt: Date.now(),
        isBatch: false
      }));


      setUploadQueue(prev => {
        const newQueue = [...prev, ...newItems];
        return newQueue;
      });

      setIsQueueVisible(true);
      setTimeout(() => processQueue(), 100);

      return newItems.map(item => item.id);
    }
  }, []);

  // Remove item from queue
  const removeFromQueue = useCallback((id) => {
    // Cancel if currently uploading
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }

    setUploadQueue(prev => prev.filter(item => item.id !== id));
    setActiveUploads(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    setUploadQueue(prev => prev.filter(item =>
      item.status !== 'completed' && item.status !== 'cancelled'
    ));
  }, []);

  // Clear all uploads
  const clearAll = useCallback(() => {
    // Cancel all active uploads
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();

    setUploadQueue([]);
    setActiveUploads(new Map());
  }, []);

  // Reorder queue items
  const moveItemUp = useCallback((id) => {
    setUploadQueue(prev => {
      const index = prev.findIndex(item => item.id === id);
      if (index <= 0) return prev;

      const newQueue = [...prev];
      [newQueue[index], newQueue[index - 1]] = [newQueue[index - 1], newQueue[index]];
      return newQueue;
    });
  }, []);

  const moveItemDown = useCallback((id) => {
    setUploadQueue(prev => {
      const index = prev.findIndex(item => item.id === id);
      if (index === -1 || index >= prev.length - 1) return prev;

      const newQueue = [...prev];
      [newQueue[index], newQueue[index + 1]] = [newQueue[index + 1], newQueue[index]];
      return newQueue;
    });
  }, []);

  // Update item status and progress
  const updateItem = useCallback((id, updates) => {
    setUploadQueue(prev => prev.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  }, []);

  // Retry failed upload
  const retryUpload = useCallback((id) => {
    updateItem(id, {
      status: 'pending',
      progress: 0,
      message: '',
      error: null
    });

    setTimeout(() => processQueue(), 100);
  }, [updateItem]);

  // Retry all failed uploads
  const retryAllFailed = useCallback(() => {
    setUploadQueue(prev => prev.map(item =>
      item.status === 'failed'
        ? { ...item, status: 'pending', progress: 0, message: '', error: null }
        : item
    ));

    setTimeout(() => processQueue(), 100);
  }, []);

  // Process upload queue with concurrency limits
  const processQueue = useCallback(async () => {

    const currentlyActive = activeUploads.size;
    const pendingItems = uploadQueue.filter(item => item.status === 'pending');

    if (pendingItems.length === 0 || currentlyActive >= concurrencyLimits.smallFiles) {
      return;
    }

    // Separate batch operations (folders) and individual files
    const pendingBatches = pendingItems.filter(item => item.isBatch);
    const pendingFiles = pendingItems.filter(item => !item.isBatch);

    // Separate large and small individual files
    const pendingLarge = pendingFiles.filter(item => item.isLarge);
    const pendingSmall = pendingFiles.filter(item => !item.isLarge);

    // Count currently uploading items by type
    const activeBatches = Array.from(activeUploads.values()).filter(item => item.isBatch).length;
    const activeLarge = Array.from(activeUploads.values()).filter(item => item.isLarge && !item.isBatch).length;
    const activeSmall = Array.from(activeUploads.values()).filter(item => !item.isLarge && !item.isBatch).length;

    const uploads = [];

    // Start batch uploads (folders) - treat as large files for concurrency
    if (activeBatches === 0 && activeLarge === 0 && pendingBatches.length > 0) {
      uploads.push(pendingBatches[0]);
    }
    // Start large file uploads (max 1 at a time, but not if batch is running)
    else if (activeLarge < concurrencyLimits.largeFiles && activeBatches === 0 && pendingLarge.length > 0) {
      uploads.push(pendingLarge[0]);
    }
    // Start small file uploads (up to remaining slots, but not if batch is running)
    else if (activeSmall < concurrencyLimits.smallFiles && activeBatches === 0) {
      const availableSlots = concurrencyLimits.smallFiles - activeSmall;
      uploads.push(...pendingSmall.slice(0, availableSlots));
    }

    // Start the uploads
    uploads.forEach(item => {
      startUpload(item);
    });
  }, [uploadQueue, activeUploads, concurrencyLimits]);

  // Callback registry for upload completion
  const uploadCompletionCallbacks = useRef(new Set());

  // Register callback for upload completion
  const onUploadComplete = useCallback((callback) => {
    uploadCompletionCallbacks.current.add(callback);

    // Return cleanup function
    return () => {
      uploadCompletionCallbacks.current.delete(callback);
    };
  }, []);

  // Trigger upload completion callbacks
  const triggerUploadComplete = useCallback(() => {
    uploadCompletionCallbacks.current.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in upload completion callback:', error);
      }
    });
  }, []);



  const cancelUpload = useCallback((id) => {

    // Get the AbortController for this specific upload
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort(); // ← This triggers the AbortSignal
    } else {
      console.warn(`⚠️ USER CANCELLATION: No controller found for upload ${id}`);
    }

    // Update UI immediately to show cancelling state
    updateItem(id, {
      status: 'cancelling',
      message: 'Cancelling upload...'
    });
  }, [updateItem]);

    const cancelUploadWithCleanup = useCallback(async (id) => {
  
  // Find the queue item
  const item = uploadQueue.find(item => item.id === id);
  if (!item) {
    console.warn(`⚠️ Queue item ${id} not found for cancellation`);
    return;
  }
  
  // Call existing cancel logic first
  cancelUpload(id);
  
  // Additional cleanup for large files with multipart data
  if (item.isLarge && item.multipart?.uploadId) {
    try {
      await fileService.abortMultipartUpload(
        item.multipart.uploadId,
        item.multipart.key,
        item.multipart.bucket,
        fileService.getToken()
      );
    } catch (error) {
      console.error(`❌ Multipart cleanup failed for ${item.multipart.uploadId}:`, error);
    }
  }

  setTimeout(() => {
    updateItem(id, {
      status: 'cancelled',
      message: 'Upload cancelled',
      error: 'Cancelled by user'
    });
  }, 100);
}, [uploadQueue, cancelUpload]);

  const startUpload = useCallback(async (item) => {

    const controller = new AbortController();
    abortControllersRef.current.set(item.id, controller);

    // Add to active uploads
    setActiveUploads(prev => new Map(prev).set(item.id, item));

    // Update status to uploading
    updateItem(item.id, {
      status: 'uploading',
      message: 'Starting upload...'
    });


    const progressCallback = (progress, message) => {
      updateItem(item.id, {
        progress: Math.min(100, Math.max(0, progress)),
        message: message || `Uploading ${item.fileName}...`
      });
    };

    try {
      let result;

      const onMultipartInit = (multipartData) => {
        updateItem(item.id, {
          multipart: multipartData
        });
      };

      if (item.isBatch && item.uploadType === 'folder') {
        result = await fileService.uploadFolderWithRename(
          item.files,
          item.targetPath,
          progressCallback,
          controller,
          onMultipartInit
        );
      } else if (item.file) {
        // Handle individual file upload        
        result = await fileService.uploadFileWithAutoRename(
          item.file,
          item.targetPath,
          progressCallback,
          controller,
          onMultipartInit
        );
      } else {
        throw new Error('Invalid upload item - no file or files array');
      }

      if (result && result.success !== false) {
        const completionMessage = item.isBatch
          ? `Folder uploaded successfully${result.wasFolderRenamed ? ` (renamed to "${result.finalFolderName}")` : ''}`
          : `Upload completed${result.wasRenamed ? ' (renamed to avoid conflict)' : ''}`;

        updateItem(item.id, {
          status: 'completed',
          progress: 100,
          message: completionMessage,
          finalFileName: item.isBatch ? result.finalFolderName : (result.finalFileName || item.fileName)
        });

        // Trigger refresh callbacks when upload completes
        setTimeout(() => {
          triggerUploadComplete();
        }, 500); // Small delay to ensure server processing
      } else {
        throw new Error(result?.error || 'Upload failed');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        updateItem(item.id, {
          status: 'cancelled',
          message: 'Upload cancelled',
          error: 'Cancelled by user'
        });
      } else {
        updateItem(item.id, {
          status: 'failed',
          message: `Upload failed: ${error.message}`,
          error: error.message
        });
      }
    } finally {
      // Remove from active uploads
      setActiveUploads(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });

      abortControllersRef.current.delete(item.id);

      // Continue processing queue
      setTimeout(() => processQueue(), 500);
    }
  }, [updateItem]);

  // Auto-process queue when items are added or status changes
  useEffect(() => {
    const timer = setTimeout(() => processQueue(), 100);
    return () => clearTimeout(timer);
  }, [uploadQueue, processQueue]);


  // Show/hide queue
  const showQueue = useCallback(() => {
    setIsQueueVisible(true);
    setIsQueueMinimized(false);
  }, []);

  const hideQueue = useCallback(() => {
    setIsQueueVisible(false);
  }, []);

  const toggleMinimized = useCallback(() => {
    setIsQueueMinimized(prev => !prev);
  }, []);

  // Get queue statistics
  const getQueueStats = useCallback(() => {
    const stats = uploadQueue.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: uploadQueue.length,
      pending: stats.pending || 0,
      uploading: stats.uploading || 0,
      completed: stats.completed || 0,
      failed: stats.failed || 0,
      cancelled: stats.cancelled || 0,
      active: activeUploads.size
    };
  }, [uploadQueue, activeUploads]);

  const value = {
    // Queue data
    uploadQueue,
    isQueueVisible,
    isQueueMinimized,
    activeUploads,

    // Queue actions
    addToQueue,
    removeFromQueue,
    clearCompleted,
    clearAll,
    moveItemUp,
    moveItemDown,
    retryUpload,
    retryAllFailed,

    // UI actions
    showQueue,
    hideQueue,
    toggleMinimized,

    // Callbacks
    onUploadComplete,
    cancelUpload,
    cancelUploadWithCleanup,

    // Utilities
    getQueueStats

  };

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export const useUploadQueue = () => {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error('useUploadQueue must be used within an UploadQueueProvider');
  }
  return context;
};

export default UploadQueueContext;